import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Divider, List, ListItem, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { ReactElement, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { EsAccountStatus } from 'common/src/types/enums/EsAccountStatus.enum';
import { IOrganization, IUser } from 'common/src/types/interfaces';
import {
  ICreateOrganizationDto,
  ICreateOrganizationWithInitialUsersDto,
  IUpdateOrganizationDto,
} from 'common/src/types/organization.dtos';
import {
  RequiredContactFields,
  requiredEsContactFields,
  requiredEsContactFieldsErrMsg,
} from 'common/src/validators/esValidationFields';

import { QUERY_KEYS } from 'portal/src/api/constants';
import {
  addOrganization,
  updateOrganization,
} from 'portal/src/api/OrganizationsService';
import useAuth from 'portal/src/auth/useAuth';
import { ExtraButtonConfig } from 'portal/src/components/common/forms/FormButtonColumn';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import FormViewFieldLayout from 'portal/src/components/common/forms/FormViewFieldLayout';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { allOrgEsContactFieldsExist } from 'portal/src/components/common/ValidateOrganizationESContactInfo';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import CreateEnergyStarAccountDialog from './CreateEnergyStarAccountDialog';
import { getEsContactFields } from './OrganizationEsContactForm.fields';
import {
  defaultValues,
  getOrganizationFields,
  OrganizationFormFields as OrganizationFormValues,
} from './OrganizationForm.fields';
import PasswordRuleOverridesForm from './PasswordRuleOverridesForm';

import Users from '../../Users';
import InitialUsers from '../User/InitialUsers';

const PrefixHints = ({ hintItems }: { hintItems: string[] }): ReactElement => (
  <List dense sx={{ typography: 'caption' }}>
    {hintItems.map((item, idx) => (
      <ListItem key={idx}>
        <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
        {item}
      </ListItem>
    ))}
  </List>
);

const OrganizationForm = ({
  entity = {},
  handleBackAction,
  isSysAdminView,
}: {
  entity?: Partial<IOrganization>;
  isSysAdminView: boolean;
  handleBackAction?: () => void;
}) => {
  const {
    hasReadOnlyAccess,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasSystemAdminAccess,
    refreshUser,
  } = useAuth();
  const snackbarHandler = useSnackbarHandler();
  const queryClient = useQueryClient();
  const isAdmin = hasAdminAccess();
  const isSysAdmin = hasSystemAdminAccess();
  const isClientSysAdmin = hasClientSystemAdminAccess();
  const isReadOnly = hasReadOnlyAccess();

  const [initialUsers, setInitialUsers] = useState<Partial<IUser>[]>([]);
  const [showCreateEsAccount, setShowCreateEsAccount] = useState(false);

  const isDeactivated = entity.id && !entity.isActive;
  const organizationFormFieldsConfig = getOrganizationFields(
    !!entity.id,
    isSysAdmin,
    isClientSysAdmin,
  );

  const {
    control,
    formState: { dirtyFields, isDirty },
    handleSubmit,
    reset,
    ...formControl
  } = useForm({ defaultValues: defaultValues(entity) });
  const { getSnackbarApiErrorMessage } = useFormControlError(formControl);
  const esContactFieldsToWatch = requiredEsContactFields.map((field) => {
    if (field === 'esContactState') {
      return 'state';
    }

    if (field === 'esContactPostalCode') {
      return 'postalCode';
    }

    return field;
  }) as (keyof OrganizationFormValues)[];

  const esContactFields = useWatch({
    control,
    name: esContactFieldsToWatch,
  });

  const esContactFieldsConfig = getEsContactFields(
    esContactFields.some((field) => !!field),
  );

  const { mutate: mutateOrg, isPending } = useMutation({
    mutationFn: async (formValues: OrganizationFormValues) => {
      const payload = prepareFormPayload(
        {
          ...formValues,
          fiscalYearStartMonth:
            formValues.fiscalYearStartMonth?.getMonth() ?? 6,
        },
        {
          ...dirtyFields,
          externalAccountIds: !isEqual(
            entity.externalAccountIds,
            formValues.externalAccountIds,
          ),
          products: !isEqual(entity.products, formValues.products),
          productFeatures: !isEqual(
            entity.productFeatures,
            formValues.productFeatures,
          ),
          passwordRuleOverrides: !isEqual(
            entity.passwordRuleOverrides,
            formValues.passwordRuleOverrides,
          ),
        },
      );

      const {
        postalCode: esContactPostalCode,
        state: esContactState,
        ...restValues
      } = payload;

      const finalPayload = {
        ...restValues,
        esContactPostalCode,
        esContactState,
      } as ICreateOrganizationDto | IUpdateOrganizationDto;

      if (entity.id) {
        await updateOrganization(entity.id, finalPayload);
        await refreshUser();
        if (!isSysAdminView) {
          reset(formValues);
        }
        snackbarHandler.success(MESSAGES.ORGANIZATION.UPDATED);
      } else {
        await addOrganization({
          organization: finalPayload,
          initialUsers,
        } as ICreateOrganizationWithInitialUsersDto);
        snackbarHandler.success(MESSAGES.ORGANIZATION.CREATED);
      }

      if (isSysAdminView) {
        handleBackAction?.();
      }
    },
    onError: (err: unknown) => {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    },
  });

  const refetchOrganization = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['organization'] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.organizations] }),
    ]);

  const resetForm = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.organizations, entity.id],
    });
    reset(defaultValues(entity));
  };

  const esExtraButtons: ExtraButtonConfig[] = [];
  const hasEsContactFields = allOrgEsContactFieldsExist(
    entity as RequiredContactFields,
  );
  const hasEsAccountError = entity.esAccountStatus === EsAccountStatus.Error;

  if ((entity.id && !entity.esAccountStatus) || hasEsAccountError) {
    esExtraButtons.push({
      isDisabled:
        !hasEsContactFields || hasEsAccountError || isPending || isReadOnly,
      label: 'Create ENERGY STAR Account',
      onClick: () => setShowCreateEsAccount(true),
      tooltipMessage: hasEsAccountError
        ? 'There was an error while creating your ENERGY STAR account; please contact support to resolve.'
        : hasEsContactFields
          ? undefined
          : 'ENERGY STAR contact fields must be filled out and saved before an account can be created',
    });
  }

  return (
    <>
      <FormContainer
        beforeMainFields={
          !entity.id && (
            <>
              <Typography variant="body2">
                The Organization ID prefix must:
              </Typography>
              <PrefixHints
                hintItems={[
                  'Be at least 2 characters in length',
                  'Only contain alphanumeric characters, spaces, and underscores',
                  'Include the key word or words that would uniquely identify the organization',
                  'Exclude generic words such as "City of", "Town of", "ISD", "School District", "SAU", "University"',
                ]}
              />
              <Typography variant="body2">Examples:</Typography>
              <PrefixHints
                hintItems={[
                  'Round Rock ISD → Round Rock',
                  'Portsmouth SAU 52 → Portsmouth',
                  'City of Cupertino → Cupertino',
                  'City of West Palm Beach → West Palm Beach',
                ]}
              />
            </>
          )
        }
        control={control}
        extraButtons={esExtraButtons.length > 0 ? esExtraButtons : undefined}
        isDirty={isDirty || (!entity.id && initialUsers.length > 0)}
        handleBackAction={isSysAdminView ? handleBackAction : undefined}
        handleSubmit={handleSubmit((data) => mutateOrg(data))}
        header="Organization Settings"
        isDeactivated={!!isDeactivated}
        isNew={!entity.id}
        isPending={isPending}
        mainFieldsConfig={organizationFormFieldsConfig}
        resetForm={isSysAdminView ? undefined : resetForm}
        afterMainFields={
          <>
            <Divider sx={{ mt: 2, mb: 1 }}>
              ENERGY STAR Contact Information
            </Divider>
            <Typography variant="body2" gutterBottom>
              This information will be used to connect the organization's ENERGY
              STAR account.
              <ListItem sx={{ typography: 'caption' }}>
                <KeyboardArrowRightIcon sx={{ width: 15, marginRight: 1 }} />
                {requiredEsContactFieldsErrMsg}
              </ListItem>
            </Typography>
            <FormViewFieldLayout
              control={control}
              mainFieldsConfig={esContactFieldsConfig}
              isDeactivated={isDeactivated}
              isReadOnly={isReadOnly}
            />
            {isClientSysAdmin && (
              <>
                <Divider sx={{ mt: 2, mb: 1 }}>Password Rule Overrides</Divider>
                <PasswordRuleOverridesForm control={control} />
              </>
            )}
            {!entity.id ? (
              <>
                <Divider sx={{ mt: 2, mb: 1 }}>Initial Users</Divider>
                <InitialUsers
                  onDataChange={setInitialUsers}
                  users={initialUsers}
                />
              </>
            ) : (
              isAdmin && (
                <>
                  <Divider sx={{ mt: 2, mb: 1 }}>Organization Users</Divider>
                  <Users
                    routeFragment={NESTED_ROUTES.USERS}
                    organizationId={isSysAdminView ? entity?.id : undefined}
                  />
                </>
              )
            )}
          </>
        }
      />
      {entity.id && (
        <CreateEnergyStarAccountDialog
          onClose={() => setShowCreateEsAccount(false)}
          onSuccess={refetchOrganization}
          open={showCreateEsAccount}
          organizationId={entity.id}
        />
      )}
    </>
  );
};

export default OrganizationForm;
